import { Clazz } from './clazz'
import { Student } from './student'
import { Teacher } from './teacher'
import { ClientStatusType } from './homework-new'
import { CompetitionScopeClazz } from './competition'
import { Drill, DrillBroadcastGymnastics } from './drill'

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

export type GenderScope = 'ALL' | 'M' | 'F'

export type RestTimeScope = 15 | 30 | 60 | 120

export const HomeworkStages = ['热身环节', '核心环节', '拉伸环节']

export const IntelligentHomeworkStages = ['热身环节', '核心环节', '拉伸环节']

export const HomeworkStageMap = {
  热身: '热身环节',
  主项目: '核心环节',
  拉伸: '拉伸环节',
}

// 作业补做类型 "yesterday_in_week"  # 本周内可补昨日（默认） "week"  # 本周内可补 "never"  # 不可补
export type HomeworkRedoConfigType = 'yesterday_in_week' | 'week' | 'never'
export const HomeworkRedoConfigYesterDayInWeek = 'yesterday_in_week'
export const HomeworkRedoConfigWeek = 'week'
export const HomeworkRedoConfigNever = 'never'

export type HomeworkStageKey = keyof typeof HomeworkStageMap

export const IntelligentHomeworkStageMap = {
  热身: '热身环节',
  主项目: '核心环节',
  拉伸: '拉伸环节',
}

export const HomeworkStageMapReverse = {
  热身环节: '热身',
  核心环节: '主项目',
  拉伸环节: '拉伸',
}

export const IntelligentHomeworkStageMapReverse = {
  热身环节: '热身',
  核心环节: '主项目',
  拉伸环节: '拉伸',
}

export type HomeworkPlanDetailsScene = 'list' | 'detail' | 'detail-weekly'

export interface HomeworkPosterData {
  start_time: string | null
  expire_time: string | null
  message: string | null
  repeatBy: HomeworkRepeatBy
  homework_contents: HomeworkContent[]
}

export interface HomeworkItem {
  drill_id: string
  drill_desc?: string | null
  repeats: number
  container_type: string
  homework_desc: string
  gender_scope: GenderScope
  stage?: HomeworkStageKey | null
  orientation?: string
  is_optional?: boolean // 是否选做
  master_type?: string // 分组类型，目前仅支撑足球，篮球，跳绳
}

export interface Homework {
  grade_no: number
  grade_homework_state: string
  homework_items: HomeworkItem[]
}

export interface HomeworkClazzStat {
  clazz: Clazz
  students_count: number
  complete_count: number
  un_complete_students: Student[]
}

export interface HomeworkClazzStatDaily extends HomeworkClazzStat {
  wday: number
  current_date: string
  is_ended: boolean
  everyday_release_id: number
}

export interface HomeworkClazzStatWeekly extends HomeworkClazzStat {
  everyday_release_ids: number[]
}

export interface HomeworkClazzDetailsStudent {
  user_id: string
  user_info: Student
  status: string
  stu_hw_state: string
  complete_time: string
}

export interface HomeworkClazzDetails {
  class: Clazz
  clazz_stat: HomeworkClazzStat
  students_complete_info: HomeworkClazzDetailsStudent[]
  homeworks: {
    homework_id: number
    homework_proto: Homework
  }[]
}

export interface HomeworkRepetition {
  weekno: Weekday
  duration: 1 | 3
}

export type HomeworkRepeatBy = 'day' | 'week' | 'weekend' | 'weekends_only'

export type HomeworkPeriod = 'single' | 'customize'

export type HomeworkStatus = 'on' | 'off'

export type HomeworkReleaseType =
  | 'normal'
  | 'winter_holiday'
  | 'summer_holiday'
  | 'activity_body_exam'
  | 'activity_summer_2022'

export interface HomeworkRelease {
  release_id: number
  start_time: string
  expire_time: string
  messages: string[]
  is_check_wd5: boolean
  period: HomeworkPeriod
  period_description: HomeworkRepetition[]
  status: HomeworkStatus
  release_type: HomeworkReleaseType
}

export interface HomeworkGroup {
  group_id: number
  status: HomeworkStatus
  is_check_wd5: boolean
  messages: string[]
  start_time: string
  expire_time: string
  repeat_type?: HomeworkRepeatBy | null
  release_type: HomeworkReleaseType
  teacher_user_id?: string
  teacher?: {
    deleted: boolean
    head_img_url: string | null
    is_admin: null
    phone: string
    role: string
    school_id: string
    teacher_id: string
    teacher_name: string
    user_id: string
  }
}

export interface HomeworkPlan {
  id: string
  client_status_type: ClientStatusType
  clazz: Clazz
  homework_contents: HomeworkContent[]
  group: HomeworkGroup
  smart_session_id: string | null
  long_term_homework_id: string | null
  title: string
  completion_start_time?: string | null // 指定作业开始时间， eg: 07:30 HH:mm
  completion_end_time?: string | null // 指定作业结束时间
}

export interface HomeworkEverydayRelease {
  everyday_release_id: number
  start_time: string
  expire_time: string
  messages: string[]
  scope_cover: 'all_students'
  status: HomeworkStatus
  teacher_user_id: string
}

export interface EverydayHomeworkPlan {
  everyday_homework_items: HomeworkItem[]
  everyday_release: HomeworkEverydayRelease
  is_currday_push_notify: boolean
  move_drill?: DrillBroadcastGymnastics
}

export interface HomeworkContent {
  homework_items: HomeworkItem[]
  day_of_week?: Weekday | null
  release_id?: number
  is_master?: boolean // 是否是专项作业， 选做 / 分组做 // update 仅分组才会设置is_master
}

export interface HomeworkPlanForm {
  schedule_id?: string
  client_status_type?: ClientStatusType
  groupId: number | null
  classes: CompetitionScopeClazz[]
  homeworkContents: HomeworkContent[]
  startDate: string | null
  endDate: string | null
  repeatBy: HomeworkRepeatBy | null
  fridayChecked: boolean
  message: string | null
  releaseType: HomeworkReleaseType
  publishToAllClasses: boolean
  template_id: string | null
  is_modify: boolean | null
  hw_tpl_id?: string
  title?: string
  is_clear_today?: boolean // 同时撤回今日已布置的作业
  completion_start_time?: string | null // 指定作业开始时间， eg: 07:30 HH:mm
  completion_end_time?: string | null // 指定作业结束时间
}

export interface HomeworkPlanGroup {
  clazz: Clazz
  homeworkPlans: HomeworkPlan[]
  winterHolidayPlan: HomeworkPlan | null
}

export type HomeworkStudentStatus = 'un_start' | 'uncompleted' | 'done' | 'delayed_done'

export type HomeworkStudentMarkScore = 'S' | 'A+' | 'A' | 'A-' | 'B' | 'C' | 'D'

export type HomeworkTeacherCheckResult =
  | 'teacher_judge_no_rope'
  | 'teacher_judge_double_jump'
  | 'teacher_judge_others'
  | 'teacher_judge_qualified'

export type HomeworkStudentSmartCheck =
  | 'normal'
  | 'no_rope'
  | 'double_jump'
  | 'no_rope_appealed'
  | 'double_jump_appealed'
  | HomeworkTeacherCheckResult

export interface HomeworkStudent {
  class_id: number
  comment_state: boolean
  curr_date: string
  everyday_homework_items?: { homework_id: number }[]
  complete_time: string | null
  stu_hw_state: HomeworkStudentStatus
  student: Student
  user_id: string
  like_state: boolean
  special_status: string | null
  remark: string | null
  mark_score: HomeworkStudentMarkScore | null
  ai_inspect_label: HomeworkStudentSmartCheck | null
  homework_total_count: number
  homework_complete_count: number
  homework_title: string // 作业模板标题
  master_type: string // 用户选择的分组作业
  is_on_leave: boolean
  leave_id: string | null // 请假ID
}

export interface HomeworkStudentWeekly {
  student: Student
  finish_rate: number
  finish_count: number
  play_count: number
  everyday_homework_items?: { homework_id: number }[]
  play_duration: number
  synthetical_level: string
  cheated: boolean
}

export interface HomeworkComment {
  create_teacher: Teacher
  comments: string
  created_time: string
}

export interface HomeworkRecord {
  id: number
  record_id: string
  drill: Drill
  user_id: string
  score: number
  score_count: number
  score_duration: number
  interrupt_count: number
  is_deleted: boolean
  is_complete: boolean
  begin_time: string
  end_time: string
  created_time: string
  mark_score: HomeworkStudentMarkScore | null
  ai_inspect_label: HomeworkStudentSmartCheck | null
  stage: string // 热身|核心|拉伸
  is_optional: boolean // 是否选做条目
  master_type: string // 分组作业所属分组
}

export interface HomeworkReportStatSummary {
  avg_complete_rate: number
  avg_jump_count: number
  avg_minute: number
}

export interface HomeworkReportTopClass {
  name: string
  complete_rate: number
  stat_detail: {
    title: string
    rank: {
      nick_name: string
      stat: number
    }[]
  }
}

export interface JumpComboRank {
  student: {
    nick_name: string
  }
  max_jumprope_combo: number
}

export interface HomeworkReport {
  stat_summary: HomeworkReportStatSummary
  excellent_classes: HomeworkReportTopClass[]
  jump_combo_rank: {
    male: JumpComboRank[]
    female: JumpComboRank[]
  }
}

export interface HomeworkStat {
  student_amount: number
  duration: number
  calories: number
  jump_rope_amount: number
  rate_s_amount: number
  rate_a_amount: number
  rate_b_amount: number
  rate_c_amount: number
  rate_d_amount: number
  unfinish_amount: number
}

export interface HomeworkDailyStat extends HomeworkStat {
  everyday_release_id: number
}

export interface HomeworkWeeklyStat extends HomeworkStat {
  everyday_release_ids: number[]
}

export enum HomeworkCommentTemplateState {
  BUITIN,
  CUSTOM,
}

export interface HomeworkCommentTemplate {
  txt: string
  state: HomeworkCommentTemplateState
}

export enum HomeworkSummaryType {
  Daily = 'daily',
  Weekly = 'weekly',
}

export interface IHomeworkSummaryQuery {
  type: HomeworkSummaryType
  date: Date
}

export interface IHomeworkDrillDetail {
  drill_id: string
  display_name: string
  record_type: string
  desc?: string
  img_url?: string
  video_url?: string
  detail_description?: string[]
  detail_rule?: string[]
  detail_criterion?: string[]
}
// demo

export interface IHomeworkIntelligentRule {
  schedule_id?: string
  smart_session_id?: string
  startDate: string | null
  endDate: string | null
  repeatBy: HomeworkRepeatBy | null
  classes: CompetitionScopeClazz[]
  class_ids: number[]
  purpose: IHomeworkIntelligentTarget | null
  rule_id: string | null
  training_duration: number
  message: string | null
  container_types: IHomeworkIntelligentRuleContainer[]
  container_type_ids: string[]
  core_training_aspect_names: string[]
  intensity: 'low' | 'medium' | 'high' | string
  phases: IHomeworkIntelligentRulePhase[]
  is_modify: boolean
  completion_start_time?: string | null // 指定作业开始时间， eg: 07:30 HH:mm
  completion_end_time?: string | null // 指定作业结束时间
}

export type IHomeworkIntelligentTarget = {
  id: string
  title: string
  generate_by: 'core_training_aspects' | 'initial'
}

export type IHomeworkIntelligentRuleOption = {
  id: string
  phases: IHomeworkIntelligentRulePhase[]
  container_types: IHomeworkIntelligentRuleContainer[]
}

export type IHomeworkIntelligentRulePhase = {
  phase: {
    id: 'string'
    title: 'string'
    subtitle: 'string'
  }
  ratio: 0
  min_drill_count: number
  max_drill_count: number
  min_repeats_per_drill: number
  max_repeats_per_drill: number
}

export type IHomeworkIntelligentRuleContainer = {
  id: string
  title: string
  container_type: string
  image_url: string
  drill_ids: string[]
  checked?: boolean
}

export type IHomeworkIntelligentRulePostParams = {
  target_id: string
  training_duration: number
}

export type HomeworkPlanDrill = {
  id: string
  img: string
  duration: number
  category: string
  container: string
  screenDirect: number
  intensity: number
  count: number
  gender: GenderScope
  equipment?: string[]
  stage?: HomeworkStageKey | null
  categories: string[]
}

export type HomeworkEditPlan = {
  id?: number
  startDate: Date
  endDate: Date
  type: HomeworkRepeatBy
  message: string
  classes: number[]
  content: HomeworkPlanDrill[][]
  template_id?: string | null
  is_modify?: boolean | null
  hw_tpl_id?: string
  title?: string
  schedule_id?: string
  completion_start_time?: string | null
  completion_end_time?: string | null
}

export const homeworkGenderScopes = [
  { value: 'ALL' as GenderScope, label: '全部' },
  { value: 'M' as GenderScope, label: '仅男生' },
  { value: 'F' as GenderScope, label: '仅女生' },
]

export const homeworkPlanTypes = [
  { value: 'day' as HomeworkRepeatBy, label: '每天重复' },
  { value: 'week' as HomeworkRepeatBy, label: '按周布置' },
  { value: 'weekend' as HomeworkRepeatBy, label: '仅周末' },
]

export const intelligentHomeworkPlanTypes = [
  { value: 'week' as HomeworkRepeatBy, label: '每天不同，按周重复' },
  { value: 'weekends_only' as HomeworkRepeatBy, label: '仅周末' },
]

export const homeworkStateText: { [key: string]: string } = {
  done: '已完成',
  delayed_done: '已补交',
  uncompleted: '未完成',
  un_start: '未开始',
}

export const smartCheckStateText: { [key: string]: string } = {
  normal: '正常',
  no_rope: '假跳',
  double_jump: '双跳',
}

export interface IAutoCorrectionConfig {
  auto_remind: boolean
  remind_at: string
  remind_times: number | null
  remind_words: string | null
  auto_like_class_ids: string[]
  auto_comment_class_ids: string[]
  operator_roles_order: string[]
}

export interface IGradeReivew {
  grade: {
    grade_id: number
    grade_no: number
    grade_name: string
  }
  status: boolean
  tpls: HomeworkCommentTemplate[]
}

export class HomeworkConstants {
  public static readonly HomeworkStageWarmUp = '热身环节'
  public static readonly HomeworkStageCore = '核心环节'
  public static readonly HomeworkStageStretching = '拉伸环节'
  public static readonly HomeworkStageOptional = '选做内容'
  public static readonly HomeworkStages = ['热身环节', '核心环节', '拉伸环节', '选做内容']
  public static readonly HomeworkStageMap: { [key: string]: string } = {
    热身: '热身环节',
    主项目: '核心环节',
    拉伸: '拉伸环节',
  }
  public static readonly HomeworkStageColor = {
    热身环节: '#FE9647',
    核心环节: '#36F',
    拉伸环节: '#7C41E3',
    选做内容: '#67D298',
  }
  public static readonly MasterTypeCnMap = {
    football: '足球',
    basketball: '篮球',
    jumprope: '跳绳',
  }
}

export enum HomeworkClazzStatSortBy {
  ClazzDesc = 'clazz_desc',
  CompleteRateDesc = 'complete_rate_desc',
}
